import React, { useCallback, useState } from 'react';
import type { FC, ReactNode } from 'react';

import { Col, Form, Link, Text, TextInput } from '@opendoor/bricks-next';

import type { LoginCredentials } from '../AuthContext';
import { useAuthAuthenticated } from '../AuthContext';

export interface ReauthFormAdditionalAuthContext {
  verifyLogin: ReturnType<typeof useAuthAuthenticated>['verifyLogin'];
  onSuccess: () => void;
  onFailure: () => void;
}

export interface ReauthFormProps {
  onSuccess: () => void;
  title?: string;
  resetPasswordHref?: string;
  analyticsPrefix?: string;
  renderAdditionalAuth?: (context: ReauthFormAdditionalAuthContext) => ReactNode;
}

/** UI for the user to reauthenticate. Commonly used before allowing the user to view sensitive data. */
export const ReauthForm: FC<ReauthFormProps> = ({
  onSuccess,
  title,
  resetPasswordHref = '/reset-password',
  analyticsPrefix = 'auth-fe-reauth',
  renderAdditionalAuth,
}) => {
  const { user, verifyLogin } = useAuthAuthenticated();
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [loginFailed, setLoginFailed] = useState(false);

  const reportLoginFailure = useCallback(() => {
    setLoginFailed(true);
  }, []);

  const verifyLoginCallback = useCallback(
    async (credentials: LoginCredentials) => {
      setLoading(true);
      setLoginFailed(false);
      try {
        const result = await verifyLogin(credentials);
        if (result.success) {
          onSuccess();
        } else {
          reportLoginFailure();
        }
      } catch {
        reportLoginFailure();
      } finally {
        setLoading(false);
      }
    },
    [onSuccess, reportLoginFailure, verifyLogin],
  );

  return (
    <Col gap="$12x">
      {title ? (
        <Text tag="h2" typography="$headerMedium">
          {title}
        </Text>
      ) : null}
      <Text tag="p" typography="$bodyMedium">
        Signing in as{' '}
        <Link
          variant="secondary"
          size="medium"
          analyticsName={`${analyticsPrefix}-user-email`}
          href={`mailto:${user.email}`}
          onPress={() => {}}
        >
          {user.email}
        </Link>
      </Text>
      <Form
        formId={`${analyticsPrefix}-form`}
        onSubmit={(event) => {
          event.preventDefault();
          void verifyLoginCallback({ email: user.email, password });
        }}
      >
        <Form.Form>
          <Col gap="$12x">
            <TextInput
              analyticsName={`${analyticsPrefix}-password`}
              id="reauth-password"
              size="medium"
              onChangeText={(value: string) => setPassword(value || '')}
              required
              textContentType="password"
              title="Password"
              errorText={
                loginFailed
                  ? 'Incorrect email or password. Use the link below to reset your password'
                  : ''
              }
            />
            <Form.Button
              variant="primary"
              size="medium"
              analyticsName={`${analyticsPrefix}-submit`}
              label="Confirm"
              aria-label="Confirm and submit password"
              loading={loading}
              disabled={loading || password.length === 0}
            />
          </Col>
        </Form.Form>
      </Form>
      {renderAdditionalAuth?.({ verifyLogin, onSuccess, onFailure: reportLoginFailure })}
      <Col alignItems="center">
        <Link
          variant="primary"
          size="medium"
          href={resetPasswordHref}
          target="_blank"
          rel="noopener noreferrer"
          analyticsName={`${analyticsPrefix}-forgot-password`}
          aria-label="Opens the forgot password page"
          onPress={() => {}}
        >
          Forgot your password?
        </Link>
      </Col>
    </Col>
  );
};
